import { useState, useRef, useEffect } from 'react';

const InspirationSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const inspirations = [
    {
      id: 1,
      image: 'https://public.readdy.ai/ai/img_res/22923ec4f322b40c64734b095a341cd1.jpg',
      title: 'Нац.парки восточной Европы'
    },
    {
      id: 2,
      image: 'https://readdy.ai/api/search-image?query=Breathtaking%20coastal%20hiking%20trail%20along%20turquoise%20Mediterranean%20sea%20with%20dramatic%20mountain%20cliffs%2C%20ancient%20Lycian%20path%2C%20scenic%20coastal%20landscape%2C%20azure%20blue%20waters%2C%20rocky%20terrain%2C%20professional%20travel%20photography%2C%20vibrant%20natural%20colors%2C%20sense%20of%20adventure&width=600&height=400&seq=inspiration-2&orientation=landscape',
      title: 'Горы и море – Ликийская тропа'
    },
    {
      id: 3,
      image: 'https://readdy.ai/api/search-image?query=Majestic%20Russian%20North%20landscape%20with%20pristine%20taiga%20forests%2C%20traditional%20wooden%20architecture%2C%20northern%20lights%20aurora%20borealis%2C%20vast%20wilderness%2C%20authentic%20rural%20village%2C%20dramatic%20sky%2C%20professional%20nature%20photography%2C%20vibrant%20natural%20colors%2C%20sense%20of%20cultural%20heritage%20and%20natural%20beauty&width=600&height=400&seq=inspiration-3&orientation=landscape',
      title: 'Русский север – природа и люди'
    },
    {
      id: 4,
      image: 'https://readdy.ai/api/search-image?query=Epic%20Far%20East%20landscape%20with%20active%20volcanic%20mountains%2C%20dramatic%20ocean%20coastline%2C%20whales%20breaching%20in%20pristine%20waters%2C%20Kamchatka%20peninsula%20scenery%2C%20snow-capped%20volcanoes%2C%20professional%20wildlife%20photography%2C%20vibrant%20natural%20colors%2C%20sense%20of%20wild%20adventure&width=600&height=400&seq=inspiration-4&orientation=landscape',
      title: 'Дальний Восток – Киты и Вулканы'
    },
    {
      id: 5,
      image: 'https://readdy.ai/api/search-image?query=Luxury%20expedition%20cruise%20ship%20sailing%20through%20pristine%20arctic%20waters%20with%20dramatic%20icebergs%2C%20turquoise%20ocean%2C%20majestic%20glaciers%2C%20adventure%20travel%20destination%2C%20professional%20maritime%20photography%2C%20vibrant%20natural%20colors%2C%20sense%20of%20exploration%20and%20luxury&width=600&height=400&seq=inspiration-5&orientation=landscape',
      title: 'Морские путешествия – экспедиции и отдых'
    },
    {
      id: 6,
      image: 'https://readdy.ai/api/search-image?query=Stunning%20mountain%20trekking%20landscape%20with%20dramatic%20alpine%20peaks%2C%20green%20meadows%2C%20hiking%20trails%2C%20adventure%20travel%20destination%2C%20pristine%20wilderness%2C%20professional%20nature%20photography%2C%20vibrant%20natural%20colors%2C%20sense%20of%20freedom%20and%20exploration&width=600&height=400&seq=inspiration-6&orientation=landscape',
      title: 'Горные походы и треккинг'
    }
  ];

  const slidesPerView = 4;
  const totalSlides = inspirations.length;
  const maxSlide = totalSlides - slidesPerView;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1));
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Свайп мышкой
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (sliderRef.current?.offsetLeft || 0));
    setScrollLeft(currentSlide);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (sliderRef.current?.offsetLeft || 0);
    const walk = (startX - x) / 100;
    
    if (Math.abs(walk) > 1) {
      if (walk > 0 && currentSlide < maxSlide) {
        setCurrentSlide(currentSlide + 1);
        setIsDragging(false);
      } else if (walk < 0 && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
        setIsDragging(false);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Свайп на тач-устройствах
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setScrollLeft(currentSlide);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const x = e.touches[0].clientX;
    const walk = (startX - x) / 100;
    
    if (Math.abs(walk) > 1) {
      if (walk > 0 && currentSlide < maxSlide) {
        setCurrentSlide(currentSlide + 1);
        setStartX(x);
      } else if (walk < 0 && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
        setStartX(x);
      }
    }
  };

  return (
    <div className="bg-white py-20">
      <div className="max-w-[1360px] mx-auto px-6">
        <h2 className="font-mulish font-bold text-3xl text-gray-900 mb-12">
          Идеи для вдохновения
        </h2>

        <div className="relative">
          {/* Слайдер */}
          <div 
            ref={sliderRef}
            className="overflow-hidden cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            <div
              className="flex gap-6 transition-transform duration-500 ease-in-out"
              style={{ 
                transform: `translateX(calc(-${currentSlide * 25}% - ${currentSlide * 1.5}rem))`,
                pointerEvents: isDragging ? 'none' : 'auto'
              }}
            >
              {inspirations.map((item) => (
                <div
                  key={item.id}
                  className="w-[calc(25%-1.125rem)] md:w-[calc(25%-1.125rem)] flex-shrink-0"
                >
                  <div className="relative h-80 rounded-lg overflow-hidden group cursor-pointer">
                    {/* Фото */}
                    <div className="w-full h-full">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        draggable="false"
                      />
                    </div>

                    {/* Градиент для читаемости текста */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                    {/* Заголовок */}
                    <div className="absolute bottom-0 left-0 p-6">
                      <h3 className="font-mulish font-bold text-xl text-white leading-tight">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Навигация и пагинация */}
          <div className="flex items-center justify-between mt-8">
            {/* Пагинация (точки) - слева */}
            <div className="flex items-center gap-2">
              {Array.from({ length: maxSlide + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    currentSlide === index
                      ? 'bg-blue-600 w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Перейти к слайду ${index + 1}`}
                />
              ))}
            </div>

            {/* Кнопки навигации - справа */}
            <div className="flex items-center gap-3">
              <button
                onClick={prevSlide}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f3f4f6] hover:bg-[#0D47A1] transition-colors duration-300 cursor-pointer group"
                aria-label="Предыдущий слайд"
              >
                <i className="ri-arrow-left-s-line text-xl text-gray-700 group-hover:text-white transition-colors duration-300"></i>
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f3f4f6] hover:bg-[#0D47A1] transition-colors duration-300 cursor-pointer group"
                aria-label="Следующий слайд"
              >
                <i className="ri-arrow-right-s-line text-xl text-gray-700 group-hover:text-white transition-colors duration-300"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Адаптивные стили для мобильных */}
      <style>{`
        @media (max-width: 768px) {
          .w-\\[calc\\(25\\%-1\\.125rem\\)\\] {
            width: 76.92% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InspirationSection;
